import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
const { FiCopy, FiCheck, FiLoader, FiAlertTriangle, FiImage } = FiIcons;
import SafeIcon from '../common/SafeIcon';

// Always use the direct worker URL for the copy button (avoids redirect chains)
const WORKER_PNG_URL = 'https://late-heart-9803.artonic.workers.dev/og-image.png';

const CopyImageButton = () => {
  const [status, setStatus] = useState('idle');
  const [ready, setReady] = useState(false);
  const [supportsClipboard, setSupportsClipboard] = useState(true);
  const blobRef = useRef(null);

  useEffect(() => {
    const hasClipboard = !!(navigator.clipboard && window.ClipboardItem);
    setSupportsClipboard(hasClipboard);

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(WORKER_PNG_URL, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.arrayBuffer();
        if (cancelled) return;
        blobRef.current = new Blob([raw], { type: 'image/png' });
      } catch (e) {
        console.warn('CopyImageButton pre-fetch failed:', e);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const fetchBlob = async () => {
    const res = await fetch(WORKER_PNG_URL, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const raw = await res.arrayBuffer();
    return new Blob([raw], { type: 'image/png' });
  };

  const handleCopy = async () => {
    if (status === 'loading') return;
    setStatus('loading');

    if (!supportsClipboard) {
      window.open(WORKER_PNG_URL, '_blank');
      setStatus('success');
      setTimeout(() => setStatus('idle'), 2500);
      return;
    }

    try {
      const pngBlob = blobRef.current ?? await fetchBlob();
      if (!blobRef.current) blobRef.current = pngBlob;

      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': pngBlob }),
      ]);
      setStatus('success');
      setTimeout(() => setStatus('idle'), 2500);
    } catch (err) {
      console.error('Copy failed:', err);
      if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
        window.open(WORKER_PNG_URL, '_blank');
        setStatus('success');
        setTimeout(() => setStatus('idle'), 2500);
      } else {
        // Last resort: open the image directly
        window.open(WORKER_PNG_URL, '_blank');
        setStatus('success');
        setTimeout(() => setStatus('idle'), 2500);
      }
    }
  };

  const label =
    status === 'loading' ? 'Copying…' :
    status === 'success' ? 'Copied!' :
    status === 'error'   ? 'Failed — retry?' :
    !supportsClipboard   ? 'Open image' :
    'Copy image';

  const bgColor =
    status === 'success' ? 'bg-green-500/80' :
    status === 'error'   ? 'bg-red-500/80' :
    'bg-black/50 hover:bg-black/70';

  const icon =
    status === 'loading' ? FiLoader :
    status === 'success' ? FiCheck :
    status === 'error'   ? FiAlertTriangle :
    !supportsClipboard   ? FiImage :
    FiCopy;

  return (
    <button
      onClick={handleCopy}
      disabled={status === 'loading' || !ready}
      title={label}
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm
        font-medium backdrop-blur-sm border border-white/20 transition-all
        duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed
        ${bgColor}
      `}
    >
      <SafeIcon
        icon={icon}
        className={`text-base ${status === 'loading' ? 'animate-spin' : ''}`}
      />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
};

export default CopyImageButton;